import { useEffect, useState } from "react";
import { MdEdit, MdSave, MdCancel } from "react-icons/md";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import Swal from "sweetalert2";
import authServiceInstance from "service/auth.service";

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email_user: "",
    password: "",
    c_password: "",
  });
  const [originalData, setOriginalData] = useState({});

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await authServiceInstance.getProfile();
        setProfile(res);
        const profileData = {
          first_name: res.first_name || "",
          last_name: res.last_name || "",
          email_user: res.email_user || "",
          password: "",
          c_password: "",
        };
        setFormData(profileData);
        setOriginalData(profileData);
      } catch (error) {
        console.error("Erreur de chargement du profil:", error);
      }
    };
    fetchProfile();
  }, []);

  const handleEditToggle = () => setIsEditing(!isEditing);

  const handleCancel = () => {
    // Restaurer les données originales
    setFormData(originalData);
    setIsEditing(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const getChangedFields = () => {
    const changedFields = {};

    // Vérifier les champs de profil
    Object.keys(formData).forEach((key) => {
      if (key === "password" || key === "c_password") {
        // Pour les mots de passe, inclure seulement s'ils ne sont pas vides
        if (formData[key] && formData[key].trim() !== "") {
          changedFields[key] = formData[key];
        }
      } else {
        // Pour les autres champs, inclure seulement s'ils ont changé
        if (formData[key] !== originalData[key]) {
          changedFields[key] = formData[key];
        }
      }
    });

    return changedFields;
  };

  const handleSave = async () => {
    try {
      const changedFields = getChangedFields();

      // Vérifier si les mots de passe correspondent
      if (changedFields.password && changedFields.c_password) {
        if (changedFields.password !== changedFields.c_password) {
          await Swal.fire({
            title: "Erreur",
            text: "Les mots de passe ne correspondent pas",
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: "#57A863",
          });
          return;
        }
      }

      // Vérifier s'il y a des changements à envoyer
      if (Object.keys(changedFields).length === 0) {
        await Swal.fire({
          title: "Information",
          text: "Aucun changement détecté",
          icon: "info",
          confirmButtonText: "OK",
          confirmButtonColor: "#57A863",
        });
        return;
      }

      // Demander confirmation avant la sauvegarde
      const result = await Swal.fire({
        title: "Confirmer les modifications",
        text: "Êtes-vous sûr de vouloir enregistrer ces modifications ?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Oui, enregistrer",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#57A863",
        cancelButtonColor: "#d33",
      });

      if (!result.isConfirmed) {
        return;
      }

      setIsLoading(true);

      await authServiceInstance.updateProfile(changedFields);

      // Mettre à jour le profil local avec les nouveaux données (sauf les mots de passe)
      const updatedProfile = { ...profile };
      Object.keys(changedFields).forEach((key) => {
        if (key !== "password" && key !== "c_password") {
          updatedProfile[key] = changedFields[key];
        }
      });

      setProfile(updatedProfile);

      // Réinitialiser les mots de passe et mettre à jour originalData
      const newFormData = {
        ...formData,
        password: "",
        c_password: "",
      };
      setFormData(newFormData);
      setOriginalData(newFormData);

      setIsEditing(false);
      setShowPassword(false);
      setShowConfirmPassword(false);

      // Afficher le message de succès
      await Swal.fire({
        title: "Succès !",
        text: "Votre profil a été mis à jour avec succès",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#57A863",
      });

      console.log("Données envoyées:", changedFields);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil:", error);
      await Swal.fire({
        title: "Erreur",
        text: "Une erreur s'est produite lors de la mise à jour du profil",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#57A863",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-[#57A863]"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Profile Card */}
      <div className="ring-black/5 overflow-hidden rounded-2xl  ring-1">
        {/* Card Header */}
        <div className="bg-gradient-to-r from-[#57A863] to-[#2F5D47] px-6 py-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">
                Informations personnelles
              </h2>
              <p className="mt-1 text-green-100">
                Modifiez vos données de profil
              </p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 md:p-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Prénom */}
            <div className="group">
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Prénom
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full rounded-lg border-2 px-4 py-3 text-gray-900 transition-all duration-200 ${
                    isEditing
                      ? "border-gray-300 focus:border-[#57A863] focus:ring-2 focus:ring-[#57A863]/20"
                      : "cursor-not-allowed border-gray-200 bg-gray-50"
                  }`}
                />
                {isEditing && (
                  <div className="absolute right-3 top-3 h-2 w-2 rounded-full bg-[#57A863]"></div>
                )}
              </div>
            </div>

            {/* Nom */}
            <div className="group">
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Nom
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full rounded-lg border-2 px-4 py-3 text-gray-900 transition-all duration-200 ${
                    isEditing
                      ? "border-gray-300 focus:border-[#57A863] focus:ring-2 focus:ring-[#57A863]/20"
                      : "cursor-not-allowed border-gray-200 bg-gray-50"
                  }`}
                />
                {isEditing && (
                  <div className="absolute right-3 top-3 h-2 w-2 rounded-full bg-[#57A863]"></div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="group md:col-span-2">
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Adresse email
              </label>
              <div className="relative">
                <input
                  type="email"
                  name="email_user"
                  value={formData.email_user}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full rounded-lg border-2 px-4 py-3 text-gray-900 transition-all duration-200 ${
                    isEditing
                      ? "border-gray-300 focus:border-[#57A863] focus:ring-2 focus:ring-[#57A863]/20"
                      : "cursor-not-allowed border-gray-200 bg-gray-50"
                  }`}
                />
                {isEditing && (
                  <div className="absolute right-3 top-3 h-2 w-2 rounded-full bg-[#57A863]"></div>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="md:col-span-2">
              <div className="my-4 flex items-center">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="bg-white px-4 text-sm text-gray-500">
                  Sécurité
                </span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>
            </div>

            {/* Mot de passe */}
            <div className="group">
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="Laisser vide pour ne pas changer"
                  className={`w-full rounded-lg border-2 px-4 py-3 pr-12 text-gray-900 transition-all duration-200 ${
                    isEditing
                      ? "border-gray-300 focus:border-[#57A863] focus:ring-2 focus:ring-[#57A863]/20"
                      : "cursor-not-allowed border-gray-200 bg-gray-50"
                  }`}
                />
                {isEditing && (
                  <>
                    <div className="absolute right-10 top-3 h-2 w-2 rounded-full bg-orange-400"></div>
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-3 text-gray-500 transition-colors hover:text-gray-700"
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible className="h-5 w-5" />
                      ) : (
                        <AiOutlineEye className="h-5 w-5" />
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Confirmation mot de passe */}
            <div className="group">
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="c_password"
                  value={formData.c_password}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="Confirmer le nouveau mot de passe"
                  className={`w-full rounded-lg border-2 px-4 py-3 pr-12 text-gray-900 transition-all duration-200 ${
                    isEditing
                      ? "border-gray-300 focus:border-[#57A863] focus:ring-2 focus:ring-[#57A863]/20"
                      : "cursor-not-allowed border-gray-200 bg-gray-50"
                  }`}
                />
                {isEditing && (
                  <>
                    <div className="absolute right-10 top-3 h-2 w-2 rounded-full bg-orange-400"></div>
                    <button
                      type="button"
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute right-3 top-3 text-gray-500 transition-colors hover:text-gray-700"
                    >
                      {showConfirmPassword ? (
                        <AiOutlineEyeInvisible className="h-5 w-5" />
                      ) : (
                        <AiOutlineEye className="h-5 w-5" />
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {isEditing ? (
                <span className="flex items-center">
                  <div className="mr-2 h-2 w-2 animate-pulse rounded-full bg-green-500"></div>
                  Mode édition activé
                </span>
              ) : (
                <span>Cliquez sur modifier pour éditer vos informations</span>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {isEditing ? (
                <>
                  {/* Bouton Annuler */}
                  <button
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="inline-flex transform items-center rounded-xl bg-gradient-to-r from-gray-500 to-gray-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:from-gray-600 hover:to-gray-700 hover:shadow-xl disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <MdCancel className="mr-2 h-4 w-4" />
                    Annuler
                  </button>

                  {/* Bouton Enregistrer */}
                  <button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="inline-flex transform items-center rounded-xl bg-gradient-to-r from-green-500 to-green-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:from-green-600 hover:to-green-700 hover:shadow-xl disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <div className="border-t-transparent mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <MdSave className="mr-2 h-4 w-4" />
                        Enregistrer les modifications
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEditToggle}
                  className="inline-flex transform items-center rounded-xl bg-gradient-to-r from-[#57A863] to-[#2F5D47] px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:from-[#2F5D47] hover:to-[#1a3d2a] hover:shadow-xl"
                >
                  <MdEdit className="mr-2 h-4 w-4" />
                  Modifier le profil
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
